import { useCallback, useEffect, useState } from 'react'

/**
 * Light / dark / system theme, persisted.
 *
 * `system` is a real third state, not a synonym for whichever mode the OS is
 * in right now: it means "keep following the OS", so the app flips when the OS
 * does. That is why the attribute is REMOVED for system rather than set to the
 * resolved value. tokens.css keys off `prefers-color-scheme` when no
 * `data-theme` is present, and off `data-theme` when it is.
 *
 * Pass `storageKey` so two tools on the same origin do not share one setting.
 */

export type Theme = 'light' | 'dark' | 'system'

const DEFAULT_STORAGE_KEY = 'boldfit-theme'

/* localStorage throws, not returns null, in Safari private mode and when a
   site is denied storage. A theme preference is not worth crashing the app for. */
function readStored(key: string): Theme {
  try {
    const stored = localStorage.getItem(key)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    /* fall through to the default */
  }
  return 'light'
}

function writeStored(key: string, theme: Theme) {
  try {
    localStorage.setItem(key, theme)
  } catch {
    /* preference is not persisted; the session still works */
  }
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'light') return 'light'
  if (theme === 'dark') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    /* Read the token rather than repeating a hex. A hardcoded pair here held
       the old warm greys and silently kept painting the phone browser chrome
       in them for a whole palette migration. */
    const background = getComputedStyle(root).getPropertyValue('--background').trim()
    if (background) meta.setAttribute('content', background)
  }
}

export function useTheme({ storageKey = DEFAULT_STORAGE_KEY }: { storageKey?: string } = {}) {
  /* Applied in the initialiser, not in the effect, so the first paint is
     already in the right theme instead of flashing light and correcting. */
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = readStored(storageKey)
    applyTheme(stored)
    return stored
  })
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    resolveTheme(readStored(storageKey)),
  )

  useEffect(() => {
    applyTheme(theme)
    writeStored(storageKey, theme)
    setResolvedTheme(resolveTheme(theme))

    if (theme !== 'system') return
    /* Only subscribe while following the OS. */
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      setResolvedTheme(resolveTheme('system'))
      applyTheme('system')
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme, storageKey])

  /** Jump straight to the opposite of what is on screen. */
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (resolveTheme(prev) === 'dark' ? 'light' : 'dark'))
  }, [])

  /** light -> dark -> system, for a single chrome button. */
  const cycleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light'))
  }, [])

  return { theme, setTheme, toggleTheme, cycleTheme, resolvedTheme }
}
